// src/components/Dashboard/timelineUtils.js
// Shared utilities for timeline gap display.

/**
 * Format a millisecond duration as a compact elapsed-time label.
 * 0-59s → "Xs", 1-59m → "Xm", 1-2h → "Xh Ym", 2h+ → "Xh"
 */
function formatElapsed(gapMs) {
  const totalSeconds = Math.round(gapMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const totalMinutes = Math.round(gapMs / 60000);
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours < 2) return `${hours}h ${mins}m`;
  return `${hours}h`;
}

/**
 * Get gap display information for the space between two consecutive events.
 * Returns label, height class for the gap row, and whether to show a date divider.
 *
 * Gap tiers:
 *   <1s  → no gap row at all (events sit right next to each other)
 *   <1m  → tiny gap with label
 *   <10m → small gap
 *   <30m → medium gap
 *   <1h  → larger gap
 *   <2h  → large gap
 *   2h+  → extra large gap
 *   24h+ → extra large gap + date divider
 *
 * @param {number} gapMs - Absolute time difference in milliseconds.
 * @returns {{ label: string|null, heightClass: string, showDateDivider: boolean }}
 */
export function getGapDisplay(gapMs) {
  if (gapMs == null || gapMs < 1000) {
    // <1s: no gap at all — events sit directly next to each other
    return { label: null, heightClass: '', showDateDivider: false };
  }

  const label = formatElapsed(gapMs);
  const minutes = gapMs / 60000;
  const hours = gapMs / 3600000;

  let heightClass;
  if (minutes < 1) heightClass = 'py-0.5';
  else if (minutes < 10) heightClass = 'py-1.5';
  else if (minutes < 30) heightClass = 'py-2.5';
  else if (hours < 1) heightClass = 'py-3.5';
  else if (hours < 2) heightClass = 'py-5';
  else if (hours < 24) heightClass = 'py-6';
  else heightClass = 'py-8';

  return { label, heightClass, showDateDivider: hours >= 24 };
}
